import * as React from "react";

import { toast } from "@replohq/lattice/components/toast/Toast";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { projectUtils } from "@v2/trpc/routes/project";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import { ReploError } from "schemas/errors";

// NOTE (Yuxin, 2026-07-18): Sentinel selection for "create a fresh workspace" rather than an existing workspace id.
export const NEW_WORKSPACE_OPTION_ID = "new";

class AgentEnableError extends ReploError {}

type AgentEnableStep = "loading" | "welcome" | "workspace" | "project";

type AgentEnableWorkspaceOption = {
  id: string;
  name: string;
  isAgentEnabled: boolean;
};

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "error"; message: string };

const errorMessage = (error: unknown): string => {
  return error instanceof Error
    ? error.message
    : "Couldn't turn on Agent. Try again.";
};

// Drives the authed part of /beta/enable: welcome pitch, pick (or create) a workspace, name a project, create it with Agent enabled.
export const useAgentEnableFlow = () => {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");
  const workspaceIdParam = searchParams.get("workspaceId");

  // null means "let the flow derive the step from the loaded workspaces".
  const [step, setStep] = React.useState<AgentEnableStep | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<
    string | null
  >(null);
  const [projectName, setProjectName] = React.useState(
    () => searchParams.get("name") ?? "",
  );
  const [submitState, setSubmitState] = React.useState<SubmitState>({
    status: "idle",
  });

  // NOTE (Yuxin, 2026-07-19): Checkpoint each created resource so a failed retry resumes instead of recreating (no orphaned workspaces/projects).
  const createdWorkspaceIdRef = React.useRef<string | null>(null);
  const createdProjectIdRef = React.useRef<string | null>(null);

  const { data: workspacesData, isLoading: isLoadingWorkspaces } =
    workspaceRouter.getUserWorkspacesList.useQuery(undefined);

  const { mutateAsync: createWorkspace } = workspaceRouter.create.useMutation();
  const { createProject } = useOnboardingCreateProject();

  const eligibleWorkspaces: AgentEnableWorkspaceOption[] = React.useMemo(() => {
    return (workspacesData?.workspaces ?? [])
      .filter((workspace) => workspace.paymentProcessor !== "shopify")
      .map((workspace) => ({
        id: workspace.id,
        name: workspace.name,
        isAgentEnabled: workspace.defaultProjectType === "agent",
      }));
  }, [workspacesData]);

  // A workspaceId query param preselects that workspace (and skips the picker) only when it's actually eligible.
  const preselectedWorkspaceId =
    eligibleWorkspaces.find((workspace) => workspace.id === workspaceIdParam)
      ?.id ?? null;

  const defaultWorkspaceId =
    preselectedWorkspaceId ??
    eligibleWorkspaces.find((workspace) => workspace.id === user?.workspace?.id)
      ?.id ??
    eligibleWorkspaces[0]?.id ??
    NEW_WORKSPACE_OPTION_ID;
  const effectiveSelectedWorkspaceId =
    selectedWorkspaceId ?? defaultWorkspaceId;

  // Changing target discards checkpoints so a later submit can't attach a stale project to the wrong workspace.
  const selectWorkspaceId = React.useCallback((workspaceId: string) => {
    setSelectedWorkspaceId((current) => {
      if (workspaceId !== current) {
        createdWorkspaceIdRef.current = null;
        createdProjectIdRef.current = null;
      }
      return workspaceId;
    });
  }, []);

  let resolvedStep: AgentEnableStep;
  if (step != null) {
    resolvedStep = step;
  } else if (isLoadingWorkspaces) {
    resolvedStep = "loading";
  } else {
    resolvedStep = "welcome";
  }

  const continueFromWelcome = React.useCallback(() => {
    logEvent("agent.beta.welcome.optedIn", { source });
    // Without eligible workspaces the picker would only offer "create new", so skip straight to naming the project.
    if (preselectedWorkspaceId || eligibleWorkspaces.length === 0) {
      setStep("project");
    } else {
      setStep("workspace");
    }
  }, [logEvent, source, preselectedWorkspaceId, eligibleWorkspaces.length]);

  const continueFromWorkspace = React.useCallback(async () => {
    const selectedWorkspace = eligibleWorkspaces.find(
      (workspace) => workspace.id === effectiveSelectedWorkspaceId,
    );

    // An already-enabled workspace has an Agent project — open it rather than creating a new one.
    if (selectedWorkspace?.isAgentEnabled) {
      setSubmitState({ status: "submitting" });
      try {
        const { projects } = await projectUtils.find.fetch({
          workspaceId: selectedWorkspace.id,
          hostingType: "replo",
          isAgentEnabled: true,
        });
        const existingProjectId = projects[0]?.id;
        if (existingProjectId) {
          navigate(
            generatePath(routes.agent.home, {
              projectId: existingProjectId,
            }),
          );
          return;
        }
      } catch {
        // No resolvable project (or fetch failed) — fall through to creating one.
      }
    }

    setSubmitState({ status: "idle" });
    setStep("project");
  }, [eligibleWorkspaces, effectiveSelectedWorkspaceId, navigate]);

  // Returns a finalize callback (launchpad navigation) on success so the card can await its animation, or null to abort.
  const submitProject = React.useCallback(async (): Promise<
    (() => void) | null
  > => {
    const trimmedProjectName = projectName.trim();
    if (!trimmedProjectName || !user) {
      return null;
    }
    setSubmitState({ status: "submitting" });
    try {
      let workspaceId = effectiveSelectedWorkspaceId;
      if (workspaceId === NEW_WORKSPACE_OPTION_ID) {
        if (!createdWorkspaceIdRef.current) {
          const created = await createWorkspace({
            name: `${trimmedProjectName} Workspace`,
            paymentProcessor: "stripe",
            eventOrigin: "onboarding",
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
          createdWorkspaceIdRef.current = created.workspace.id;
        }
        workspaceId = createdWorkspaceIdRef.current;
      }

      // Create before flipping so a failed flip still leaves a usable project; never pass workspaceName (it renames the workspace).
      if (!createdProjectIdRef.current) {
        const result = await createProject({
          shopName: trimmedProjectName,
          workspaceId,
          hostingType: "replo",
          userEmail: user.email,
          isAgentEnabled: true,
        });
        const createdProjectId = result.project?.id;
        if (!createdProjectId) {
          throw new AgentEnableError({
            message: "Project was created without an id",
          });
        }
        createdProjectIdRef.current = createdProjectId;
      }
      const projectId = createdProjectIdRef.current;

      return () => {
        navigate(generatePath(routes.agent.home, { projectId }));
      };
    } catch (error) {
      const message = errorMessage(error);
      setSubmitState({ status: "error", message });
      toast.error({ title: "Couldn't turn on Agent", description: message });
      return null;
    }
  }, [
    createProject,
    createWorkspace,
    effectiveSelectedWorkspaceId,
    navigate,
    projectName,
    user,
  ]);

  return {
    step: resolvedStep,
    source,
    submitState,
    eligibleWorkspaces,
    selectedWorkspaceId: effectiveSelectedWorkspaceId,
    projectName,
    setSelectedWorkspaceId: selectWorkspaceId,
    setProjectName,
    continueFromWelcome,
    continueFromWorkspace,
    submitProject,
    goBackToWorkspace: () => {
      setSubmitState({ status: "idle" });
      setStep("workspace");
    },
  };
};
